/**
 * PropertiesBoards — Iter-157 · Phase 3.
 * Four dedicated boards mounted as separate tabs across every
 * department command center (Finance, HR, Legal, Sales, CS):
 *   1. BillingARBoard             — AR aging + payment status
 *   2. PropertiesPricingBoard     — pricing per property/contract
 *                                   rate + month-to-month premium
 *   3. PropertiesContractsBoard   — active contracts + renewal stage
 *   4. PropertiesServicesAgreement— full service agreement scope
 *
 * Every row also surfaces Payment Method / Statement / Contract Period
 * where applicable to the department's role.
 */
import { useEffect, useState } from 'react';
import { CommercialHandoffBoard } from './CommercialHandoffBoard';
import { getAdminViewAs } from '../../lib/adminScopeTransport';
// Iter-160 · Phase 5 — Full payment method suite on the PM Billing tab.
import PaymentMethodsPanel from './PaymentMethodsPanel';
// Iter-163 · Phase 5 — Unified billing notifications panel embedded
// on the PM Billing tab. Replaces the standalone PM bell.
import UnifiedBillingNotifications from './UnifiedBillingNotifications';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';
const STATUS_COLORS = {
  GREEN: '#10b981', BLUE: '#3b82f6', YELLOW: '#facc15', AMBER: '#f59e0b',
  ORANGE: '#f97316', RED: '#ef4444', BLACK: '#111827',
};
const STATUS_TEXT_LIGHT = { YELLOW: '#111', AMBER: '#111' };

// Iter-191 · Escalate to Owner — inline button rendered on past-due
// A/R rows. Fires POST /api/billing/invoices/{id}/escalate which
// emails + SMSes the Owner and marks the invoice priority=high.
function EscalateButton({ invoiceId, invoiceNumber }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  async function fire() {
    const note = window.prompt('Add a short note for the Owner (optional):', '') || '';
    setBusy(true);
    setErr('');
    try {
      const res = await fetch(`${API}/api/billing/invoices/${invoiceId}/escalate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Escalation failed.');
      setDone(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (done) {
    return <span className="text-emerald-400 text-[10px] font-bold" data-testid={`billing-ar-escalated-${invoiceNumber}`}>✓ Sent to Owner</span>;
  }
  return (
    <>
      <button
        type="button"
        onClick={fire}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
        data-testid={`billing-ar-escalate-${invoiceNumber}`}
        title="Escalate this past-due invoice directly to the property Owner (email + SMS + inbox alert)."
      >
        {busy ? 'Sending…' : 'Escalate to Owner'}
      </button>
      {err && <div className="text-[10px] text-red-400 mt-1" title={err}>Failed</div>}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
/*  Board 1 — Billing & A/R                                          */
/* ═════════════════════════════════════════════════════════════════ */
export function BillingARBoard({ pmContext } = {}) {
  const [snap, setSnap] = useState(null);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        // Iter-160 · Phase 5 — refresh status colors before rendering so
        // grace period + due-today transitions are always up to date.
        await fetch(`${API}/api/iter156/billing/refresh-status`, { credentials: 'include', method: 'POST' }).catch(() => {});
        const [d, l] = await Promise.all([
          fetch(`${API}/api/iter156/finance/dashboard`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
          fetch(`${API}/api/iter156/invoices?status=`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { invoices: [] }),
        ]);
        setSnap(d); setInvoices(l.invoices || []);
      } catch (_) {}
    })();
  }, []);
  if (!snap) return <p className="text-zinc-500 text-sm" data-testid="billing-ar-loading">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="billing-ar-board">
      {/* Iter-160 · Phase 5 — full payment method suite on PM view.
          Renders only when the board is mounted inside a PM dashboard
          (pmContext provides the property + customer). Iter-164 ·
          Phase 3 — show a clear setup-incomplete banner instead of
          silently hiding the panel when property_id is missing. */}
      {pmContext && !pmContext.property_id && (
        <div className="rounded-lg border p-4"
             style={{ backgroundColor: '#3f1d1d', borderColor: '#dc2626' }}
             data-testid="pm-account-setup-incomplete">
          <p className="text-sm font-bold text-white uppercase tracking-widest">Account setup incomplete</p>
          <p className="text-xs text-red-100 mt-1">
            No property is linked to your account, so payment methods cannot be shown.
            Please contact your NECS administrator to complete your account setup.
          </p>
        </div>
      )}
      {pmContext?.property_id && (
        <PaymentMethodsPanel
          property_id={pmContext.property_id}
          customer_id={pmContext.customer_id}
          customer_email={pmContext.customer_email}
        />
      )}
      {/* Iter-163 · Phase 5 — unified billing notifications for the PM. */}
      {pmContext?.property_id && (
        <UnifiedBillingNotifications scope="pm" property_id={pmContext.property_id} />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Outstanding', `$${Number(snap.outstanding).toLocaleString()}`],
          ['Collected', `$${Number(snap.collected).toLocaleString()}`],
          ['MRR', `$${Number(snap.mrr).toLocaleString()}`],
          ['Invoices', String(snap.invoice_count)],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 border border-zinc-800 bg-[#141414]" data-testid={`billing-ar-kpi-${k.toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">{k}</p>
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{v}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: GOLD }}>AR AGING</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['current','1_30','31_60','61_90','over_90'].map((k) => (
            <div key={k} className="rounded-lg p-3 border border-zinc-800 bg-[#141414]" data-testid={`billing-ar-aging-${k}`}>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400">{k.replace('_','-')}</p>
              <p className="text-lg font-bold text-white">${Number((snap.ar_aging || {})[k] || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-700">
        <table className="admin-stack-table text-sm" data-testid="billing-ar-table">
          <thead className="bg-[#141414] text-zinc-400 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Invoice</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Property</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Payment Method</th>
              <th className="text-right p-3">Total</th>
              <th className="text-left p-3">Due Date</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-zinc-500">No invoices on file.</td></tr>}
            {invoices.slice(0, 50).map((r) => {
              const color = r.status_color || 'GREEN';
              const label = r.status_label || color;
              const isPastDue = String(r.status || '').toLowerCase().includes('past_due')
                              || String(color).toUpperCase() === 'RED'
                              || String(label).toLowerCase().includes('past due');
              return (
                <tr key={r.invoice_number} className="border-t border-zinc-800" data-testid={`billing-ar-row-${r.invoice_number}`}>
                  <td className="p-3 font-bold" data-label="Invoice" style={{ color: GOLD }}>{r.invoice_number}</td>
                  <td className="p-3 text-white" data-label="Customer">{r.customer_legal_name}</td>
                  <td className="p-3 text-zinc-300" data-label="Property">{r.property_name || '—'}</td>
                  <td className="p-3 text-center" data-label="Status">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
                          style={{ backgroundColor: STATUS_COLORS[color] || '#333', color: STATUS_TEXT_LIGHT[color] || '#fff' }}
                          data-testid={`billing-ar-status-${r.invoice_number}`}>
                      {label}
                    </span>
                  </td>
                  <td className="p-3 text-center text-zinc-300 text-xs uppercase" data-label="Payment Method">
                    {r.payment_method === 'card' ? 'Card autopay'
                      : r.payment_method === 'check' ? 'Check'
                      : r.autopay ? 'ACH autopay'
                      : 'ACH · Card · Check · Wire'}
                  </td>
                  <td className="p-3 text-right text-white" data-label="Total">${Number(r.total).toLocaleString()}</td>
                  <td className="p-3 text-zinc-200 font-semibold" data-label="Due Date" data-testid={`billing-ar-due-${r.invoice_number}`}>{(r.due_date || '').slice(0, 10)}</td>
                  {/* Iter-191 · Escalate to Owner — visible only on
                      past-due rows. Sends email + SMS + in-app note to
                      the Owner and marks the invoice priority=high. */}
                  <td className="p-3 text-right" data-label="Actions">
                    {isPastDue ? (
                      <EscalateButton invoiceId={r.invoice_id || r.invoice_number} invoiceNumber={r.invoice_number} />
                    ) : (
                      <span className="text-[10px] text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
/*  Board 2 — Properties Pricing                                     */
/* ═════════════════════════════════════════════════════════════════ */
export function PropertiesPricingBoard() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          fetch(`${API}/api/properties`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/contracts`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        const contractsByProperty = {};
        (Array.isArray(cRes) ? cRes : []).forEach((c) => {
          const pid = c.property_id || c.propertyId;
          if (pid) contractsByProperty[pid] = c;
        });
        const props = (Array.isArray(pRes) ? pRes : []).slice(0, 200).map((p) => {
          const c = contractsByProperty[p.property_id || p.id] || {};
          return {
            property_id: p.property_id || p.id,
            property_name: p.property_name || p.name,
            service_type: p.property_type || p.service_type || '—',
            monthly_rate: c.monthly_amount || c.monthly_rate || p.monthly_rate || 0,
            m2m: !!(c.month_to_month || c.is_month_to_month),
            m2m_premium_pct: c.month_to_month_premium_pct || 15,
            contract_start: (c.start_date || c.effective_date || '—').slice(0, 10),
            contract_end: (c.end_date || c.contract_end || '—').slice(0, 10),
            payment_method: c.payment_method || 'ACH · Card · Check · Wire',
            statement: c.statement_url ? 'View statement' : 'Statement pending',
          };
        });
        setRows(props);
      } catch (_) {}
    })();
  }, []);
  return (
    <div className="space-y-3" data-testid="properties-pricing-board">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">Contract rate + month-to-month premium per property. Payment method and statement summary shown at row level.</p>
      <div className="overflow-x-auto rounded-lg border border-zinc-700">
        <table className="w-full text-sm" data-testid="properties-pricing-table">
          <thead className="bg-[#141414] text-zinc-400 text-[10px] uppercase tracking-widest">
            <tr><th className="text-left p-3">Property</th><th className="text-left p-3">Type</th><th className="text-right p-3">Contract Rate</th><th className="text-center p-3">M2M</th><th className="text-center p-3">Payment Method</th><th className="text-left p-3">Statement</th><th className="text-left p-3">Contract Period</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-zinc-500">No properties returned.</td></tr>}
            {rows.map((r) => (
              <tr key={r.property_id} className="border-t border-zinc-800" data-testid={`properties-pricing-row-${r.property_id}`}>
                <td className="p-3 text-white">{r.property_name || r.property_id}</td>
                <td className="p-3 text-zinc-300 uppercase text-xs">{r.service_type}</td>
                <td className="p-3 text-right text-white">${Number(r.monthly_rate).toLocaleString()}/mo</td>
                <td className="p-3 text-center">
                  {r.m2m ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-orange-500 text-black">PREMIUM +{r.m2m_premium_pct}%</span> : <span className="text-[10px] text-zinc-500">Fixed</span>}
                </td>
                <td className="p-3 text-center text-xs text-zinc-300 uppercase">{r.payment_method}</td>
                <td className="p-3 text-xs text-zinc-400">{r.statement}</td>
                <td className="p-3 text-xs text-zinc-400">{r.contract_start} → {r.contract_end}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
/*  Board 3 — Properties Contracts                                   */
/* ═════════════════════════════════════════════════════════════════ */
export function PropertiesContractsBoard() {
  const [contracts, setContracts] = useState([]);
  const [alerts, setAlerts] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const c = await fetch(`${API}/api/contracts`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []);
        setContracts(Array.isArray(c) ? c : []);
        const a = await fetch(`${API}/api/iter156/renewals/alerts`, { credentials: 'include' }).then((r) => r.ok ? r.json() : { windows: {} });
        setAlerts(a.windows || {});
      } catch (_) {}
    })();
  }, []);
  const stageFor = (id) => {
    for (const w of [30, 60, 90, 120, 180]) {
      if ((alerts[String(w)] || []).some((r) => r.contract_id === id)) return w;
    }
    return null;
  };
  return (
    <div className="space-y-3" data-testid="properties-contracts-board">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">Active contract per property with term (12/24/36 or M2M), start, end, renewal stage, and payment method.</p>
      <div className="overflow-x-auto rounded-lg border border-zinc-700">
        <table className="w-full text-sm" data-testid="properties-contracts-table">
          <thead className="bg-[#141414] text-zinc-400 text-[10px] uppercase tracking-widest">
            <tr><th className="text-left p-3">Contract</th><th className="text-left p-3">Property</th><th className="text-center p-3">Term</th><th className="text-left p-3">Contract Period</th><th className="text-center p-3">Renewal Stage</th><th className="text-center p-3">Payment Method</th></tr>
          </thead>
          <tbody>
            {contracts.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-zinc-500">No contracts on file.</td></tr>}
            {contracts.slice(0, 100).map((c) => {
              const id = c.contract_id || c.id;
              const stage = stageFor(id);
              const term = c.term_months || c.contract_months;
              const m2m = !!c.month_to_month || !!c.is_month_to_month;
              return (
                <tr key={id} className="border-t border-zinc-800" data-testid={`properties-contracts-row-${id}`}>
                  <td className="p-3 font-bold" style={{ color: GOLD }}>{id}</td>
                  <td className="p-3 text-zinc-300">{c.property_name || c.property_id}</td>
                  <td className="p-3 text-center text-white">{m2m ? 'M2M' : `${term || 12}m`}</td>
                  <td className="p-3 text-xs text-zinc-400">{(c.start_date || '—').slice(0,10)} → {(c.end_date || '—').slice(0,10)}</td>
                  <td className="p-3 text-center">
                    {stage ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: stage <= 30 ? '#ef4444' : stage <= 60 ? '#f97316' : stage <= 90 ? '#facc15' : '#3b82f6', color: stage <= 60 ? '#fff' : '#111' }}>{stage}-day</span> : <span className="text-[10px] text-zinc-500">—</span>}
                  </td>
                  <td className="p-3 text-center text-xs text-zinc-300 uppercase">{c.payment_method || 'ACH · Card · Check · Wire'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════ */
/*  Board 4 — Properties & Services Agreement                        */
/* ═════════════════════════════════════════════════════════════════ */
export function PropertiesServicesAgreementBoard() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          fetch(`${API}/api/properties`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/api/contracts`, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
        ]);
        const byId = {};
        (Array.isArray(cRes) ? cRes : []).forEach((c) => { if (c.property_id) byId[c.property_id] = c; });
        setRows((Array.isArray(pRes) ? pRes : []).slice(0, 200).map((p) => ({
          ...p, contract: byId[p.property_id || p.id] || {},
        })));
      } catch (_) {}
    })();
  }, []);
  return (
    <div className="space-y-3" data-testid="properties-services-agreement-board">
      <CommercialHandoffBoard viewAs={getAdminViewAs()} />
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">Full agreement scope per property: service lines, frequency, specialty services, payment method, statement summary.</p>
      <div className="grid gap-3">
        {rows.length === 0 && <p className="text-center text-zinc-500 text-sm p-6" data-testid="properties-services-empty">No agreements returned.</p>}
        {rows.map((p) => {
          const c = p.contract || {};
          const services = p.services_selected || p.service_lines || (c.services || []);
          const specialty = p.specialty_services || c.specialty_services || [];
          const freq = c.frequency || p.service_frequency || 'Recurring';
          return (
            <div key={p.property_id || p.id} className="rounded-lg border border-zinc-800 bg-[#141414] p-4" data-testid={`properties-services-agreement-row-${p.property_id || p.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <p className="text-white font-bold uppercase tracking-widest" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{p.property_name || p.name}</p>
                  <p className="text-[10px] text-zinc-400 uppercase mt-0.5">{p.property_type || '—'} · {p.city || ''} {p.state || ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase text-zinc-500">Contract Period</p>
                  <p className="text-xs text-white">{(c.start_date || '—').slice(0,10)} → {(c.end_date || '—').slice(0,10)}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-xs">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Service Lines</p>
                  <div className="flex flex-wrap gap-1">
                    {(services || []).length === 0 && <span className="text-zinc-500">—</span>}
                    {(Array.isArray(services) ? services : []).map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ backgroundColor: 'rgba(201,169,98,0.15)', color: GOLD }}>{typeof s === 'string' ? s : (s?.name || 'service')}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Frequency</p>
                  <p className="text-zinc-200">{freq}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Specialty Services</p>
                  <div className="flex flex-wrap gap-1">
                    {(specialty || []).length === 0 && <span className="text-zinc-500">—</span>}
                    {(Array.isArray(specialty) ? specialty : []).map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ backgroundColor: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>{typeof s === 'string' ? s : (s?.name || 'specialty')}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Payment Method · Statement</p>
                  <p className="text-zinc-200 text-xs">{c.payment_method || 'ACH · Card · Check · Wire'}</p>
                  <p className="text-[10px] text-zinc-500">{c.statement_url ? 'Statement on file' : 'Statement pending'}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
